import _ from 'lodash';
import { watch } from 'vue';
import { defineStore } from 'pinia';
import log from 'loglevel';

import { useUserStore } from './UserStore';
import { PayoutStore } from './PayoutStore';
import { OfferStatusStore } from './OfferStatusStore';
import { UserActivityStore } from './UserActivityStore';
import { NotificationType } from '@/constants/NotificationType';
import INotification from '@/interfaces/Notifications/INotification';
import NotificationService from '@/api/services/NotificationService';
import { isUserLoggedIn } from '@/utils/utils';

export const NotificationsStore = defineStore('NotificationsStore', {
    state: () => ({
        notifications: [] as INotification[],
        unsubscribe: null as (() => void) | null,
        isLoading: false,
    }),
    getters: {
        /**
         * Returns the count of unread notifications
         */
        unreadCount(): number {
            return this.notifications.filter(notification => !notification.readAt).length;
        },
        
        /**
         * Returns whether there are any notifications
         */
        hasNotifications(): boolean {
            return this.notifications.length > 0;
        }
    },
    actions: {
        /**
         * Initializes the notifications store
         * Sets up the notifications listener and watchers
         */
        async initialize() {
            console.log('Initializing NotificationsStore');
            
            if (this.isLoading) {
                log.info("NotificationsStore: Already initializing");
                return false;
            }
            
            this.isLoading = true;
            
            try {
                const userStore = useUserStore();
                
                // Check if user exists and has an ID before proceeding
                if (!userStore.user || !userStore.user.id) {
                    log.warn("NotificationsStore: Cannot initialize - user ID not available");
                    return false;
                }
                
                // Clean up existing listener if any
                this.stopListening();
                
                log.info(`Setting up notifications listener for user ID: ${userStore.user.id}`);
                
                // Bind the handleNotificationsUpdate method to this instance
                const boundHandleNotificationsUpdate = this.handleNotificationsUpdate.bind(this);
                
                // Get the notifications listener
                this.unsubscribe = await NotificationService.getNotificationsListener(
                    userStore.user.id, 
                    boundHandleNotificationsUpdate
                );

                // Set up watchers for notifications changes
                this.setupWatchers();
                
                console.log('NotificationsStore initialized successfully');
                return true;
            } catch (error) {
                log.error("Failed to initialize NotificationsStore:", error);
                return false;
            } finally {
                this.isLoading = false;
            }
        },
        
        /**
         * Handles notifications updates from the listener
         * @param notifications - The updated notifications array
         */
        handleNotificationsUpdate(notifications: INotification[]) {
            log.info(`Received ${notifications.length} notifications update`);
            
            // Sort notifications by creation date (newest first)
            this.notifications = notifications.sort((a, b) => {
                return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
            });
            
            if (notifications.length === 0) {
                log.info("No notifications found for the user");
            }
        },
        
        /**
         * Stops the notifications listener
         */
        stopListening() {
            if (this.unsubscribe) {
                log.info("Stopping notifications listener");
                this.unsubscribe();
                this.unsubscribe = null;
            }
        },
        
        /**
         * Marks all notifications as read
         */
        async markAllAsRead() {
            try {
                const userStore = useUserStore();
                
                if (!userStore.user || !userStore.user.id) {
                    log.warn("Cannot mark notifications as read - user ID not available");
                    return;
                }
                
                log.info("Marking all notifications as read");
                await NotificationService.markAllNotificationsAsRead(userStore.user.id);
                
                // Update local state to reflect changes immediately
                this.notifications = this.notifications.map(notification => ({
                    ...notification,
                    readAt: notification.readAt || new Date().toISOString()
                }));
            } catch (error) {
                log.error("Failed to mark all notifications as read:", error);
            }
        },
        
        /**
         * Marks a specific notification as read
         * @param notificationId - The ID of the notification to mark as read
         */
        async markNotificationAsRead(notificationId: string) {
            try {
                const userStore = useUserStore();
                
                if (!userStore.user || !userStore.user.id) {
                    log.warn("Cannot mark notification as read - user ID not available");
                    return;
                }
                
                log.info(`Marking notification ${notificationId} as read`);
                await NotificationService.markNotificationAsRead(userStore.user.id, notificationId);
                
                // Update local state to reflect changes immediately
                this.notifications = this.notifications.map(notification => 
                    notification.id === notificationId 
                        ? { ...notification, readAt: new Date().toISOString() } 
                        : notification
                );
            } catch (error) {
                log.error(`Failed to mark notification ${notificationId} as read:`, error);
            }
        },
        
        /**
         * Sets up watchers for notifications changes
         * Updates related stores when notifications change
         */
        setupWatchers() {
            const payoutStore = PayoutStore();
            const offerStatusStore = OfferStatusStore();
            const userActivityStore = UserActivityStore();
            const userStore = useUserStore();

            watch(() => this.notifications, (newNotifications, oldNotifications) => {
                // Skip if notifications haven't changed
                if (_.isEqual(newNotifications, oldNotifications)) {
                    return;
                }
                
                // Create a Set to store the types of notifications that have been processed
                const processedNotificationTypes = new Set();

                newNotifications.forEach(notification => {
                    // If this type of notification has already been processed, skip it
                    if (processedNotificationTypes.has(notification.notificationType)) {
                        return;
                    }

                    switch (notification.notificationType) {
                        case NotificationType.PayoutStatus:
                            payoutStore.getMyPayoutData();
                            break;
                        case NotificationType.InhouseTask:
                            userActivityStore.getEarnData();
                            break;
                        case NotificationType.OffersCallback:
                            offerStatusStore.getAllMyOffers();
                            break;
                        case NotificationType.SpinTheWheel:
                            userActivityStore.getEarnData(); // Update earnings list for spin wheel rewards
                            break;
                        default:
                            log.debug(`No specific action for notification type: ${notification.notificationType}`);
                    }

                    // Refresh user data for all notification types
                    userStore.getUser();

                    // Add this type of notification to the set of processed types
                    processedNotificationTypes.add(notification.notificationType);
                });
            }, { deep: true });
        },
        
        /**
         * Manually checks for notifications
         * Reinitializes the notifications listener
         */
        async checkForNotifications() {
            if (this.isLoading) {
                log.info("Already checking for notifications");
                return;
            }
            
            this.isLoading = true;
            
            try {
                const userStore = useUserStore();
                
                // Check if user exists and has an ID before proceeding
                if (!userStore.user || !userStore.user.id) {
                    log.warn("Cannot check for notifications - user ID not available");
                    return;
                }
                
                log.info("Manually checking for notifications");
                
                // Reset initialization state and stop existing listener
                this.stopListening();
                
                log.info("Manual notification check complete");
            } catch (error) {
                log.error("Failed to manually check for notifications:", error);
            } finally {
                this.isLoading = false;
            }
        },
        
        /**
         * Resets the store to its initial state
         */
        reset() {
            this.stopListening();
            this.$reset();
        }
    }
});
